"use client"

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ChartConfig } from '@/components/ui/chart'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { requestMobile } from '@/lib/request'
import { processTrendData } from '@/lib/trendDataProcessor'
import { RefreshCw, Filter, X } from 'lucide-react'
import { usePersistentState } from '@/hooks/usePersistentState'

interface ChartDataItem {
  name: string
  value: number
}

const chartConfig = {
  value: {
    label: '数值',
    color: 'var(--chart-1)',
  },
} satisfies ChartConfig

export default function MobileTrend() {
  const [platform, setPlatform] = usePersistentState('mobile:trend:platform', 'xr')
  const [timeRange, setTimeRange] = usePersistentState('mobile:trend:time-range', '3h')
  const [chartData, setChartData] = useState<Record<string, ChartDataItem[]>>({})
  const [loading, setLoading] = useState(false)
  const [showFilter, setShowFilter] = useState(false)

  const fetchData = async () => {
    setLoading(true)
    try {
      const resp = await requestMobile('/common/apis/v1/trend', {
        plat: platform,
        h: timeRange,
      })

      // Check if this is ES response format
      if (resp?.data?.hits?.hits) {
        // Process ES data using the processor
        const processedData = processTrendData(resp.data, platform)

        // Format data for charts - only top4s and maxMiny2
        const formattedData: Record<string, ChartDataItem[]> = {}

        const chartKeys = ['top4s', 'maxMiny2']
        chartKeys.forEach((key) => {
          if (processedData[key] && Array.isArray(processedData[key])) {
            formattedData[key] = processedData.x.map((time: string, index: number) => ({
              name: time,
              value: processedData[key][index] || 0,
            }))
          }
        })

        setChartData(formattedData)
      } else if ((resp as any)?.x && Array.isArray((resp as any).x)) {
        // Data already processed, use directly
        const formattedData: Record<string, ChartDataItem[]> = {}
        const respData = resp as any

        const chartKeys = ['top4s', 'maxMiny2']
        chartKeys.forEach((key) => {
          if (respData[key] && Array.isArray(respData[key])) {
            formattedData[key] = respData.x.map((time: string, index: number) => ({
              name: time,
              value: respData[key][index] || 0,
            }))
          }
        })

        setChartData(formattedData)
      }
    } catch (error) {
      console.error('Failed to fetch trend data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [platform])

  const renderChart = (chartKey: string, label: string) => {
    const data = chartData[chartKey] || []

    if (data.length === 0) {
      return null
    }

    const hasValidData = data.some(item => item.value !== 0)

    return (
      <Card key={chartKey} className="w-full border-primary/30 bg-primary/5">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-mono font-semibold text-primary">
            {label} {!hasValidData && <span className="text-xs text-muted-foreground ml-2">(无数据)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <ChartContainer config={chartConfig} className="h-[200px] w-full">
            <LineChart data={data} margin={{ left: 8, right: 8, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={40}
                tickFormatter={(value) => value.slice(11, 16)}
                style={{ fontSize: '10px' }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => value.toLocaleString()}
                style={{ fontSize: '10px' }}
                width={45}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Line
                type="natural"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="min-h-full bg-background">
      {/* 头部 */}
      <div className="bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b" style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}>
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-xl font-bold">趋势分析</h1>
            <p className="text-sm text-muted-foreground">{platform} - {timeRange}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilter(!showFilter)}
              className="relative"
            >
              <Filter className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* 筛选面板 */}
        {showFilter && (
          <div className="border-t border-border p-4 space-y-3 bg-muted/30">
            {/* 平台筛选 */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">平台</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xr">xr</SelectItem>
                  <SelectItem value="xl">xl</SelectItem>
                  <SelectItem value="cm">cm</SelectItem>
                  <SelectItem value="mg">mg</SelectItem>
                  <SelectItem value="al">al</SelectItem>
                  <SelectItem value="cx">cx</SelectItem>
                  <SelectItem value="nx">nx</SelectItem>
                  <SelectItem value="qc">qc</SelectItem>
                  <SelectItem value="lc">lc</SelectItem>
                  <SelectItem value="bxj">bxj</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 时间范围 */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">时间范围</Label>
              <Input
                placeholder="如: 3h"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              />
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                className="flex-1"
                onClick={() => setShowFilter(false)}
              >
                完成
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 图表列表 */}
      <div className="p-4 space-y-4">
        {loading && Object.keys(chartData).length === 0 ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">加载中...</p>
            </div>
          </div>
        ) : (
          <>
            {renderChart('top4s', 'top4s')}
            {renderChart('maxMiny2', 'maxMiny2')}
          </>
        )}

        {!loading && Object.keys(chartData).length === 0 && (
          <div className="flex items-center justify-center h-[300px]">
            <p className="text-muted-foreground">暂无数据</p>
          </div>
        )}
      </div>
    </div>
  )
}
